/**
 * VisualizerModal — Full-featured modal for advanced visualizer controls.
 *
 * Opened from SidebarVisualizer. Contains:
 *   - Larger preview canvas
 *   - Visualization picker grid
 *   - Mode selector (Manual / Song / Timed)
 *   - Global settings (brightness, crossfade, interval)
 *   - Per-visualization settings
 *   - Rotation pool manager
 *   - Audio source controls
 *   - Audio insight panel
 *   - Projection window button
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Play, Pause, SkipForward, SkipBack, Shuffle, Monitor, Mic, MonitorOff,
  Eye, EyeOff, Snowflake, SunDim, Gauge, Maximize2, Volume2, Save, Paintbrush, Pencil
} from 'lucide-react';
import { VisualizationEngine, DEFAULT_VISUALIZER_STATE, saveVisualizerDefaults, clearVisualizerDefaults } from '../VisualizationEngine';
import { getAllVisualizationsWithCustom } from '../visualizations';
import { VisualizerState, PlaybackMode, VisualizationSetting, Visualization } from '../types';
import { loadCustomPresets } from '../custom/storage';
import { CustomVisualizationPreset } from '../custom/types';
import CustomVizBuilderModal from '../custom/builder/CustomVizBuilderModal';
import toast from 'react-hot-toast';

interface VisualizerModalProps {
  engine: VisualizationEngine | null;
  state: VisualizerState;
  audioStatus: 'none' | 'system' | 'microphone';
  onStartAudio: (source: 'system' | 'microphone') => Promise<void>;
  onStopAudio: () => void;
  onOpenProjection: () => void;
  onClose: () => void;
}

export default function VisualizerModal({
  engine, state, audioStatus, onStartAudio, onStopAudio, onOpenProjection, onClose,
}: VisualizerModalProps) {
  const modalCanvasRef = useRef<HTMLCanvasElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [showPool, setShowPool] = useState(false);
  const [vizSettings, setVizSettings] = useState<VisualizationSetting[]>([]);
  const [tab, setTab] = useState<'visualizations' | 'settings'>('visualizations');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingPreset, setEditingPreset] = useState<CustomVisualizationPreset | undefined>();
  const [vizListVersion, setVizListVersion] = useState(0);

  // Get all visualizations (built-in + custom) — refreshes when vizListVersion changes
  const allVizs: Visualization[] = getAllVisualizationsWithCustom();
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  vizListVersion; // reference to trigger re-render

  // Load per-viz settings when active visualization changes
  useEffect(() => {
    const viz = allVizs.find(v => v.id === state.activeVisualizationId);
    setVizSettings(viz?.getSettings?.() ?? []);
  }, [state.activeVisualizationId, vizListVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach a second canvas for the modal preview
  useEffect(() => {
    const canvas = modalCanvasRef.current;
    const container = modalContainerRef.current;
    if (!engine || !canvas || !container) return;

    // The engine is already running on the sidebar canvas.
    // For the modal, we mirror by drawing the sidebar canvas onto this one each frame.
    let animFrame: number;
    const draw = () => {
      animFrame = requestAnimationFrame(draw);
      const sourceCanvas = (engine as any).canvas as HTMLCanvasElement | null;
      if (!sourceCanvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      ctx.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
    };
    draw();

    return () => cancelAnimationFrame(animFrame);
  }, [engine]);

  const updateVizSetting = useCallback((key: string, value: number | string | boolean) => {
    const viz = allVizs.find(v => v.id === state.activeVisualizationId);
    if (viz?.setSettings) {
      viz.setSettings({ [key]: value });
      setVizSettings(viz.getSettings?.() ?? []);
    }
  }, [state.activeVisualizationId, allVizs]);

  const activeViz = allVizs.find(v => v.id === state.activeVisualizationId);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-gray-900 rounded-xl border border-gray-700 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700/50 bg-gray-800/60">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-white">Visualizer Controls</span>
            {state.running && (
              <span className="px-1.5 py-0.5 text-[10px] bg-green-500/20 text-green-400 rounded font-medium">
                LIVE
              </span>
            )}
            {audioStatus !== 'none' && (
              <span className="px-1.5 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 rounded font-medium">
                {audioStatus === 'system' ? 'System Audio' : 'Mic'}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Preview canvas */}
          <div ref={modalContainerRef} className="relative w-full h-52 bg-black rounded-lg overflow-hidden">
            <canvas
              ref={modalCanvasRef}
              className="w-full h-full block"
            />
            {!state.running && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 gap-2">
                <span className="text-sm text-gray-400">Audio visualizer</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onStartAudio('system')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600/80 hover:bg-purple-600 text-white text-xs font-medium transition-colors"
                  >
                    <Monitor className="w-3.5 h-3.5" /> System Audio
                  </button>
                  <button
                    onClick={() => onStartAudio('microphone')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-600/80 hover:bg-gray-600 text-white text-xs font-medium transition-colors"
                  >
                    <Mic className="w-3.5 h-3.5" /> Microphone
                  </button>
                </div>
              </div>
            )}
            {state.showFps && state.running && (
              <span className="absolute top-1 right-2 text-[10px] font-mono text-green-400/70">
                {engine?.getFps() ?? 0} fps
              </span>
            )}
          </div>

          {/* Playback + quick toggles bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => engine?.prevVisualization()}
                disabled={!state.running}
                className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-30 transition-colors"
                title="Previous (P)"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              {state.running ? (
                <button
                  onClick={() => { engine?.stop(); onStopAudio(); }}
                  className="p-2 rounded-lg bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
                  title="Stop"
                >
                  <Pause className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (audioStatus !== 'none') {
                      engine?.start();
                    } else {
                      onStartAudio('system');
                    }
                  }}
                  className="p-2 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                  title="Start"
                >
                  <Play className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => engine?.nextVisualization()}
                disabled={!state.running}
                className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-30 transition-colors"
                title="Next (N)"
              >
                <SkipForward className="w-4 h-4" />
              </button>
              <button
                onClick={() => engine?.randomVisualization()}
                disabled={!state.running}
                className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-30 transition-colors"
                title="Random (R)"
              >
                <Shuffle className="w-4 h-4" />
              </button>
              <div className="w-px h-5 bg-gray-700 mx-1" />
              <button
                onClick={() => engine?.updateState({ blackout: !state.blackout })}
                disabled={!state.running}
                className={`p-2 rounded-lg transition-colors disabled:opacity-30 ${state.blackout ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                title="Blackout (B)"
              >
                {state.blackout ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => engine?.updateState({ frozen: !state.frozen })}
                disabled={!state.running}
                className={`p-2 rounded-lg transition-colors disabled:opacity-30 ${state.frozen ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                title="Freeze (F)"
              >
                <Snowflake className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              {/* Audio source controls */}
              {audioStatus !== 'none' && (
                <button
                  onClick={onStopAudio}
                  className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
                  title="Disconnect audio"
                >
                  <MonitorOff className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onOpenProjection}
                className="p-2 rounded-lg bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 transition-colors"
                title="Open Projection Window"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Current viz info */}
          {activeViz && (
            <div className="text-xs text-gray-400">
              <span className="text-gray-500">Active:</span>{' '}
              <span className="text-white font-medium">{activeViz.name}</span>
              <span className="text-gray-500"> — {activeViz.description}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setTab('visualizations')}
              className={`px-4 py-2 text-xs font-medium transition-colors ${
                tab === 'visualizations'
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Visualizations
            </button>
            <button
              onClick={() => setTab('settings')}
              className={`px-4 py-2 text-xs font-medium transition-colors ${
                tab === 'settings'
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Settings
            </button>
          </div>

          {/* Tab content */}
          {tab === 'visualizations' && (
            <div className="space-y-3">
              {/* Visualization picker grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                {allVizs.map(viz => (
                  <button
                    key={viz.id}
                    onClick={() => engine?.setVisualization(viz.id)}
                    className={`px-2.5 py-2 rounded-lg text-xs text-left transition-colors relative group ${
                      state.activeVisualizationId === viz.id
                        ? 'bg-purple-600 text-white ring-1 ring-purple-400'
                        : 'bg-gray-800/60 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <div className="font-medium truncate">{viz.name}</div>
                    <div className="text-[9px] text-gray-400 truncate mt-0.5">{viz.category}</div>
                    {/* Edit button for custom visualizations */}
                    {viz.category === 'custom' && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          const presetId = viz.id.replace('custom-', '');
                          const preset = loadCustomPresets().find(p => p.id === presetId);
                          if (preset) {
                            setEditingPreset(preset);
                            setShowBuilder(true);
                          }
                        }}
                        className="absolute top-1 right-1 p-0.5 rounded bg-gray-700/80 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit custom visualization"
                      >
                        <Pencil size={10} />
                      </button>
                    )}
                  </button>
                ))}
                {/* Create Custom button */}
                <button
                  onClick={() => { setEditingPreset(undefined); setShowBuilder(true); }}
                  className="px-2.5 py-2 rounded-lg text-xs text-left border-2 border-dashed border-gray-700 text-gray-400 hover:border-purple-500 hover:text-purple-300 transition-colors flex items-center gap-1.5"
                >
                  <Paintbrush size={14} />
                  <div>
                    <div className="font-medium">Create Custom</div>
                    <div className="text-[9px] text-gray-500 mt-0.5">Build your own</div>
                  </div>
                </button>
              </div>

              {/* Rotation pool manager */}
              <div>
                <button
                  onClick={() => setShowPool(!showPool)}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                >
                  {showPool ? 'Hide' : 'Manage'} Rotation Pool ({state.poolIds.length}/{allVizs.length})
                </button>

                {showPool && (
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    {allVizs.map(viz => (
                      <label key={viz.id} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer px-2 py-1 rounded hover:bg-gray-800">
                        <input
                          type="checkbox"
                          checked={state.poolIds.includes(viz.id)}
                          onChange={e => {
                            const poolIds = e.target.checked
                              ? [...state.poolIds, viz.id]
                              : state.poolIds.filter(id => id !== viz.id);
                            engine?.updateState({ poolIds });
                          }}
                          className="rounded"
                        />
                        {viz.name}
                        <span className="text-gray-500 text-[9px]">({viz.category})</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'settings' && (
            <div className="space-y-4">
              {/* Mode selector */}
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1.5">Rotation Mode</label>
                <div className="flex gap-1.5">
                  {([
                    { mode: 'manual', label: 'Manual', desc: 'Change manually' },
                    { mode: 'auto-song', label: 'Per Song', desc: 'Change with each song' },
                    { mode: 'auto-timed', label: 'Timed', desc: 'Auto-rotate on interval' },
                  ] as { mode: PlaybackMode; label: string; desc: string }[]).map(({ mode, label, desc }) => (
                    <button
                      key={mode}
                      onClick={() => engine?.updateState({ mode })}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors ${
                        state.mode === mode
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium">{label}</div>
                      <div className="text-[9px] mt-0.5 opacity-60">{desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Timed interval (only for auto-timed mode) */}
              {state.mode === 'auto-timed' && (
                <div>
                  <label className="text-[10px] text-gray-400 uppercase tracking-wider">
                    Interval: {state.timedIntervalSec}s
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={300}
                    step={5}
                    value={state.timedIntervalSec}
                    onChange={e => engine?.updateState({ timedIntervalSec: +e.target.value })}
                    className="w-full h-1.5 mt-1 accent-purple-500"
                  />
                  <div className="flex justify-between text-[9px] text-gray-500">
                    <span>10s</span><span>5min</span>
                  </div>
                </div>
              )}

              {/* Crossfade */}
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider">
                  Crossfade: {state.crossfadeSec}s
                </label>
                <input
                  type="range"
                  min={0}
                  max={3}
                  step={0.5}
                  value={state.crossfadeSec}
                  onChange={e => engine?.updateState({ crossfadeSec: +e.target.value })}
                  className="w-full h-1.5 mt-1 accent-purple-500"
                />
                <div className="flex justify-between text-[9px] text-gray-500">
                  <span>Off</span><span>3s</span>
                </div>
              </div>

              {/* Brightness */}
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <SunDim className="w-3 h-3" /> Brightness: {Math.round(state.brightness * 100)}%
                </label>
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={state.brightness}
                  onChange={e => engine?.updateState({ brightness: +e.target.value })}
                  className="w-full h-1.5 mt-1 accent-purple-500"
                />
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.showFps}
                    onChange={e => engine?.updateState({ showFps: e.target.checked })}
                    className="rounded"
                  />
                  <Gauge className="w-3 h-3" /> Show FPS
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.performanceMode}
                    onChange={e => engine?.updateState({ performanceMode: e.target.checked })}
                    className="rounded"
                  />
                  Performance Mode
                </label>
              </div>

              {/* Per-visualization settings */}
              {vizSettings.length > 0 && (
                <div className="border-t border-gray-700 pt-3">
                  <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-2">
                    {activeViz?.name} Settings
                  </label>
                  {vizSettings.map(s => (
                    <div key={s.key} className="mb-2">
                      <label className="text-xs text-gray-400">
                        {s.label}: {typeof s.value === 'number' ? s.value : String(s.value)}
                      </label>
                      {s.type === 'range' && (
                        <input
                          type="range"
                          min={s.min}
                          max={s.max}
                          step={s.step}
                          value={s.value as number}
                          onChange={e => updateVizSetting(s.key, +e.target.value)}
                          className="w-full h-1.5 accent-purple-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Audio insight panel (when running with audio) */}
          {state.running && audioStatus !== 'none' && (
            <AudioInsight engine={engine} />
          )}
        </div>

        {/* Footer — Save/Reset defaults */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700/50 flex-shrink-0">
          <button
            onClick={() => {
              clearVisualizerDefaults();
              engine?.updateState({ ...DEFAULT_VISUALIZER_STATE, running: state.running });
              toast.success('Visualizer reset to defaults');
            }}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            Reset Defaults
          </button>
          <button
            onClick={() => {
              saveVisualizerDefaults(state);
              toast.success('Visualizer defaults saved');
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-purple-300 bg-purple-500/20 hover:bg-purple-500/30 rounded transition-colors font-medium"
          >
            <Save className="w-3 h-3" />
            Save as Default
          </button>
        </div>
      </div>

      {/* Custom Visualization Builder */}
      {showBuilder && (
        <CustomVizBuilderModal
          editPreset={editingPreset}
          onClose={() => { setShowBuilder(false); setEditingPreset(undefined); }}
          onSaved={() => setVizListVersion(v => v + 1)}
        />
      )}
    </div>
  );
}

// ── Audio Insight Sub-component ──────────────────────────────────────────────

function AudioInsight({ engine }: { engine: VisualizationEngine | null }) {
  const [data, setData] = useState({ bass: 0, mid: 0, treble: 0, bpm: 0, isBeat: false });

  useEffect(() => {
    if (!engine) return;
    let animFrame: number;
    const poll = () => {
      animFrame = requestAnimationFrame(poll);
      // Access the engine's last frame data via the analyzer
      const analyzer = (engine as any).analyzer;
      if (analyzer) {
        const frame = analyzer.getFrameData();
        setData({
          bass: frame.bassEnergy,
          mid: frame.midEnergy,
          treble: frame.trebleEnergy,
          bpm: frame.bpm,
          isBeat: frame.isBeat,
        });
      }
    };
    poll();
    return () => cancelAnimationFrame(animFrame);
  }, [engine]);

  return (
    <div className="flex items-center gap-3 bg-gray-800 rounded-lg px-3 py-2">
      <Volume2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <div className="flex items-center gap-3 flex-1">
        <EnergyMeter label="Bass" value={data.bass} color="bg-red-400" />
        <EnergyMeter label="Mid" value={data.mid} color="bg-yellow-400" />
        <EnergyMeter label="Treble" value={data.treble} color="bg-blue-400" />
      </div>
      {data.bpm > 0 && (
        <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">{Math.round(data.bpm)} BPM</span>
      )}
      <div className={`w-2.5 h-2.5 rounded-full transition-colors flex-shrink-0 ${data.isBeat ? 'bg-white' : 'bg-gray-600'}`} />
    </div>
  );
}

function EnergyMeter({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[8px] text-gray-500 uppercase">{label}</div>
      <div className="w-full h-1.5 bg-gray-700 rounded-full mt-0.5">
        <div
          className={`h-full rounded-full ${color} transition-all duration-100`}
          style={{ width: `${Math.max(2, value * 100)}%` }}
        />
      </div>
    </div>
  );
}
